import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { tokens } from '../../theme';
import PersonIcon from '@mui/icons-material/Person';
import { useSettings } from '../../contexts/SettingsContext';
import {
  getADPRound,
  getBlockedReason,
  getKeeperRound,
  getKeeperValue,
  getLastYearDraftRound,
  isBlockedByRules,
} from './keeperRules';

const KeeperTeamTile = ({ owner, keeperPlayers = [], leagueSettings, onClick }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { positionColors } = useSettings();

  // Environment-specific debugging helper
  const debugKeeperSettings = (settings, context = '') => {
    const envInfo = {
      environment: process.env.NODE_ENV,
      context,
      settings: {
        keeper_max_years_kept: settings?.keeper_max_years_kept,
        keeper_highest_round: settings?.keeper_highest_round,
        keeper_round_cost: settings?.keeper_round_cost,
        keeper_undrafted_round: settings?.keeper_undrafted_round
      },
      defaults_applied: {
        keeper_max_years_kept: settings?.keeper_max_years_kept ?? 2,
        keeper_highest_round: settings?.keeper_highest_round ?? 3,
        keeper_round_cost: settings?.keeper_round_cost ?? 2,
        keeper_undrafted_round: settings?.keeper_undrafted_round ?? 10
      }
    };
    
    // console.log(`[KeeperTeamTile] ${context} Environment Debug:`, envInfo);
    
    // Specifically flag missing undrafted round setting
    if (!settings?.keeper_undrafted_round) {
      console.warn(`[KeeperTeamTile] 🚨 PRODUCTION ISSUE: keeper_undrafted_round is ${settings?.keeper_undrafted_round}, defaulting to 10`);
    }
    
    return envInfo;
  };

  // Early return if colors are not properly initialized
  if (!colors || !colors.primary) {
    return null;
  }

  // Debug keeper settings on component load
  debugKeeperSettings(leagueSettings, 'Component Load');

  // Verify and enforce league settings defaults
  const verifiedLeagueSettings = {
    ...leagueSettings,
    keeper_round_cost: leagueSettings?.keeper_round_cost ?? 2,
    keeper_highest_round: leagueSettings?.keeper_highest_round ?? 3,
    keeper_undrafted_round: leagueSettings?.keeper_undrafted_round ?? 10,
    keeper_max_years_kept: leagueSettings?.keeper_max_years_kept ?? 2,
  };

  // Use verified settings for all calculations
  const effectiveLeagueSettings = verifiedLeagueSettings;

  // Helper functions to access nested player data and calculate values
  const getPlayerName = (player) => {
    return player.player?.name || player.name || `Player ${player.sleeper_id}`;
  };
  
  const getPlayerPosition = (player) => {
    return player.player?.position || player.position;
  };
  
  const getPlayerTeam = (player) => {
    return player.player?.team || player.team;
  };
  
  // Get team count for ADP calculation
  const teamCount = effectiveLeagueSettings?.total_rosters || 12;

  const keeperValueOf = (player) => getKeeperValue(player, { teamCount });

  // Sort players by keeper value (highest value first, non-keepable last)
  const sortedPlayers = [...keeperPlayers].sort((a, b) => {
    const valueA = keeperValueOf(a);
    const valueB = keeperValueOf(b);

    // If neither can be priced, maintain original order
    if (valueA === null && valueB === null) return 0;

    // Unpriceable players go to the end
    if (valueA === null) return 1;
    if (valueB === null) return -1;
    
    // Sort by keeper value (higher is better)
    return valueB - valueA;
  });

  // Calculate keeper stats
  const keepablePlayersList = keeperPlayers.filter((player) => getKeeperRound(player) !== null);
  const keeperCount = keepablePlayersList.length;
  const totalPlayersCount = keeperPlayers.length;
  
  // Calculate total keeper value (positive values only)
  const totalKeeperValue = keeperPlayers.reduce((sum, player) => {
    const value = keeperValueOf(player);
    return sum + (value > 0 ? value : 0);
  }, 0);

  // Get position color helper
  const getPositionColor = (position) => {
    if (!positionColors) {
      const fallbackColors = {
        QB: "rgba(239, 116, 161, 0.8)",
        RB: "rgba(143, 242, 202, 0.8)",
        WR: "rgba(86, 201, 248, 0.8)",
        TE: "rgba(254, 174, 88, 0.8)",
        K: "rgba(182, 185, 255, 0.8)",
        DEF: "rgba(191, 117, 93, 0.8)",
        DST: "rgba(191, 117, 93, 0.8)",
      };
      return fallbackColors[position] || "rgba(128, 128, 128, 0.8)";
    }
    return positionColors[position] || positionColors["DEFAULT"] || "rgba(128, 128, 128, 0.8)";
  };

  // Format values for display
  const formatKeeperValue = (value) => {
    if (!value || value === "0" || value === 0) return "Not Keepable";
    return `Round ${value}`;
  };

  const formatADPValue = (value) => {
    if (!value || value === "0" || value === 0) return "N/A";
    return `Round ${value}`;
  };

  // Last season's raw draft round. Undrafted is a real, meaningful answer here --
  // it's why the player falls back to the undrafted keeper round -- so say so
  // rather than showing "N/A".
  const formatDraftRound = (value) => {
    if (value === null || value === undefined) return "Undrafted";
    return `R${value}`;
  };

  // Which season the draft round came from. The API reports it per player; they're
  // all the same season, so read it off the first one that has it.
  const lastDraftSeasonLabel =
    keeperPlayers.find((p) => p.last_year_draft_season)?.last_year_draft_season ??
    "last season";

  return (
    <Card
      onClick={onClick}
      sx={{
        backgroundColor: colors.primary[400],
        borderRadius: 2,
        border: `1px solid ${colors.grey[700]}`,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.3s ease-in-out',
        '&:hover': {
          transform: onClick ? 'translateY(-2px)' : 'none',
          boxShadow: onClick ? `0 4px 15px ${alpha(colors.blueAccent[500], 0.2)}` : 'none',
          borderColor: onClick ? colors.blueAccent[500] : colors.grey[700],
        },
      }}
    >
      <CardContent sx={{ flex: 1, p: 3 }}>
        {/* Header with Owner Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            src={owner?.avatar ? `https://sleepercdn.com/avatars/thumbs/${owner.avatar}` : null}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: colors.blueAccent[500],
              mr: 2,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                color: colors.grey[100],
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {owner?.display_name || owner?.username || 'Unknown Owner'}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Chip
                label={`${keeperCount} Keepable Players`}
                size="small"
                sx={{
                  backgroundColor: alpha(colors.greenAccent[500], 0.2),
                  color: colors.greenAccent[400],
                  fontSize: '0.75rem',
                  height: 20,
                }}
              />
              {totalKeeperValue > 0 && (
                <Chip
                  label={`Total Value: ${Math.round(totalKeeperValue)}`}
                  size="small"
                  sx={{
                    backgroundColor: alpha(colors.blueAccent[500], 0.2),
                    color: colors.blueAccent[400],
                    fontSize: '0.75rem',
                    height: 20,
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Simplified Player List - only showing keeper round and ADP round */}
        <Box sx={{ flex: 1 }}>
          {totalPlayersCount === 0 ? (
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[500],
                textAlign: 'center',
                fontStyle: 'italic',
                py: 2,
              }}
            >
              No players found
            </Typography>
          ) : (
            <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
              {sortedPlayers.map((player, index) => (
                <Box
                  key={player.sleeper_id || player.player_id || index}
                  sx={{
                    backgroundColor: alpha(getPositionColor(getPlayerPosition(player)), 0.1),
                    borderRadius: 1,
                    border: `1px solid ${alpha(getPositionColor(getPlayerPosition(player)), 0.3)}`,
                    p: 1.5,
                    mb: 1,
                  }}
                >
                  {/* Player Info Row */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.grey[100],
                        fontWeight: 600,
                        fontSize: '0.875rem',
                        flexShrink: 0,
                      }}
                    >
                      {getPlayerName(player)}
                    </Typography>
                    <Chip
                      label={getPlayerPosition(player)}
                      size="small"
                      sx={{
                        backgroundColor: alpha(getPositionColor(getPlayerPosition(player)), 0.8),
                        color: colors.primary[900],
                        fontWeight: 600,
                        fontSize: '0.65rem',
                        height: 18,
                        flexShrink: 0,
                      }}
                    />
                    {getPlayerTeam(player) && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: colors.grey[400],
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          flexShrink: 0,
                        }}
                      >
                        {getPlayerTeam(player)}
                      </Typography>
                    )}
                    {isBlockedByRules(player) && (
                      <Chip
                        label="Ineligible"
                        size="small"
                        title={getBlockedReason(player) || undefined}
                        sx={{
                          backgroundColor: alpha(colors.redAccent[500], 0.2),
                          color: colors.redAccent[400],
                          fontSize: '0.6rem',
                          height: 16,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>

                  {/* Drafted / Keeper & ADP Info Row */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[400], fontSize: '0.6rem' }}
                      >
                        Drafted:
                      </Typography>
                      <Chip
                        label={formatDraftRound(getLastYearDraftRound(player))}
                        size="small"
                        title={
                          getLastYearDraftRound(player) === null
                            ? `Undrafted in ${lastDraftSeasonLabel}`
                            : `Round ${getLastYearDraftRound(player)} of the ${lastDraftSeasonLabel} draft`
                        }
                        sx={{
                          backgroundColor: getLastYearDraftRound(player)
                            ? alpha(colors.blueAccent[500], 0.2)
                            : alpha(colors.grey[500], 0.2),
                          color: getLastYearDraftRound(player)
                            ? colors.blueAccent[300]
                            : colors.grey[400],
                          fontSize: '0.65rem',
                          height: 18,
                          fontWeight: 600,
                          ml: 1,
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[400], fontSize: '0.6rem' }}
                      >
                        Keeper:
                      </Typography>
                      <Chip
                        label={formatKeeperValue(getKeeperRound(player))}
                        size="small"
                        sx={{
                          backgroundColor: getKeeperRound(player) 
                            ? alpha(colors.greenAccent[500], 0.2) 
                            : alpha(colors.redAccent[500], 0.2),
                          color: getKeeperRound(player) 
                            ? colors.greenAccent[400] 
                            : colors.redAccent[400],
                          fontSize: '0.65rem',
                          height: 18,
                          fontWeight: 600,
                          ml: 1,
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: colors.grey[400], fontSize: '0.6rem' }}
                      >
                        ADP:
                      </Typography>
                      <Chip
                        label={formatADPValue(getADPRound(player, teamCount))}
                        size="small"
                        sx={{
                          backgroundColor: alpha(colors.blueAccent[500], 0.2),
                          color: colors.blueAccent[400],
                          fontSize: '0.65rem',
                          height: 18,
                          fontWeight: 600,
                          ml: 1,
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default KeeperTeamTile;